import dayjs from 'dayjs';
import { refundStatus, refundStatusOptions } from 'Models/Enums';
import { store } from 'Models/Store';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { processManualRefund } from 'Services/Api/_HumanWritten/BookingService/BookingService';
import { formatCurrency } from 'Util/_HumanWritten/CurrencyUtils';
import { formatStandardDate, formatStandardDateTime } from 'Util/_HumanWritten/DateUtils';
import { formatPriceDisplay } from 'Util/_HumanWritten/PriceFormattingUtils';
import { formatStandardTime } from 'Util/_HumanWritten/TimeUtils';
import alertToast from 'Util/ToastifyUtils';
import { stringIsEmpty, stringNotEmpty } from 'Util/TypeGuards';
import { CustomTooltip } from 'Views/Components/_HumanWritten/CustomTooltip/CustomTooltip';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import If from 'Views/Components/If/If';
import { newConfirmModal } from 'Views/Components/Modal/NewConfirmModal';
import { Button, Colors, Display, Sizes } from 'Views/Components/Button/Button';
import { MessageBox, MessageBoxStatus } from 'Views/Components/_HumanWritten/MessageBox/MessageBox';
import { ManualRefundModal, RefundData } from './Modals/ManualRefundModal';
import { CancelManualRefundModal } from './Modals/CancelManualRefundModal';
import { ConvertRefundToCreditModal } from './Modals/ConvertRefundToCreditModal';

/**
 * Refunds come back from the API untyped. Only the status needs a precise type here so it can key the
 * refund status labels; the remaining fields stay open until the refund DTO is typed properly.
 */
interface Refund {
	status: refundStatus;
	/** Set when this refund is paying out a credit voucher. */
	giftCertificateId?: string | null;
	[key: string]: any;
}

interface RefundsSectionProps {
	pendingRefunds: any[];
	refresh: () => void;
}

interface RefundItemProps {
	refund: Refund;
	showButton?: boolean;
	onButtonClick?: () => void;
	showCancelButton?: boolean;
	showConvertButton?: boolean;
	onConvertClick?: () => void;
	onCancelClick?: () => void;
}

function RefundInformationContent({ refund }: { refund: any }) {
	return (
		<div className="refund__information-box">
			<h5>{formatCurrency(refund.amount)}</h5>
			<div className="refund__information">
				<p className="refund__item refund__type">
					<If condition={stringIsEmpty(refund.providerRefundId)}>
						<Icon name="credit_card_gear" useMaterialIcons />
						Manual refund
						<CustomTooltip
							content={[
								<FormattedMessage
									key="manual-refund-tooltip"
									id="booking-success__refund-card__info"
									defaultMessage="Staff will contact you to issue your refund in the next few days."
								/>,
							]}
						/>
					</If>
					<If condition={stringNotEmpty(refund.providerRefundId)}>
						<Icon name="credit_card" useMaterialIcons />
						Card refund ({refund.cardBrand} ••••{refund.last4})
						<CustomTooltip
							content={[
								<FormattedMessage
									key="card-refund-tooltip"
									id="booking-success__provider-refund-card__info"
									defaultMessage="Refund will take 5 - 10 business days to reach your account."
								/>,
							]}
						/>
					</If>
				</p>
				<p className="refund__item refund__created-date">
					<Icon name="schedule" useMaterialIcons />
					Requested: {formatStandardTime(refund.created)} {formatStandardDate(refund.created)}
				</p>
			</div>
		</div>
	);
}

function RefundItem({
	refund,
	showButton = false,
	onButtonClick,
	showCancelButton = false,
	onCancelClick,
	showConvertButton = false,
	onConvertClick,
}: RefundItemProps) {
	const commonProps = {
		key: refund.id,
		title: refundStatusOptions[refund.status],
		children: <RefundInformationContent refund={refund} />,
		status: (refund.status === 'FAILED' ? 'error' : 'warning') as MessageBoxStatus,
	};

	if ((showButton && onButtonClick) || (showCancelButton && onCancelClick) || (showConvertButton && onConvertClick)) {
		return (
			<MessageBox
				{...commonProps}
				actions={
					<div className="refund__actions">
						<If condition={showButton && !!onButtonClick}>
							<Button
								onClick={onButtonClick}
								display={Display.Outline}
								colors={Colors.Black}
								sizes={Sizes.Small}
								newStyles
							>
								Set to refunded
							</Button>
						</If>
						<If condition={showConvertButton && !!onConvertClick}>
							<Button
								onClick={onConvertClick}
								display={Display.Outline}
								colors={Colors.Black}
								sizes={Sizes.Small}
								newStyles
							>
								Convert to credit
							</Button>
						</If>
						<If condition={showCancelButton && !!onCancelClick}>
							<Button
								onClick={onCancelClick}
								display={Display.Outline}
								colors={Colors.Red}
								sizes={Sizes.Small}
								newStyles
							>
								Cancel refund
							</Button>
						</If>
					</div>
				}
			/>
		);
	}

	// Restored by hand: the codemod that migrated this file dropped the {...commonProps} spread here.
	return <MessageBox {...commonProps} />;
}

export function RefundsSection({ pendingRefunds, refresh }: RefundsSectionProps) {
	const [isExpanded, setIsExpanded] = React.useState(false);
	const [showRefundModal, setShowRefundModal] = React.useState(false);
	const [selectedRefund, setSelectedRefund] = React.useState<RefundData | null>(null);
	const [showCancelRefundModal, setShowCancelRefundModal] = React.useState(false);
	const [selectedCancelRefund, setSelectedCancelRefund] = React.useState<RefundData | null>(null);
	const [showConvertRefundModal, setShowConvertRefundModal] = React.useState(false);
	const [selectedConvertRefund, setSelectedConvertRefund] = React.useState<RefundData | null>(null);

	if (pendingRefunds.length === 0) {
		return null;
	}

	const totalRefundAmount = pendingRefunds.reduce((sum, refund) => sum + refund.amount, 0);

	const renderRefunds = () => {
		// The capability process-manual-refund carries. Its floor was moved to Manager to match what
		// this control has always shown, so the same people see it as before.
		if (store.can('IssueManualRefund')) {
			return pendingRefunds.map(refund => (
				<RefundItem
					key={refund.id}
					refund={refund}
					showButton={refund.status === 'PENDING_MANUAL' || refund.status === 'FAILED'}
					onButtonClick={() => {
						setSelectedRefund({
							id: refund.id,
							amount: refund.amount,
						});
						setShowRefundModal(true);
					}}
					// Cancel-refund action stays hidden — it's a separate feature, not part of
					// convert-to-credit. Re-enable with:
					// refund.status === 'PENDING_MANUAL' && stringIsEmpty(refund.providerRefundId)
					showCancelButton={false}
					onCancelClick={() => {
						setSelectedCancelRefund({
							id: refund.id,
							amount: refund.amount,
						});
						setShowCancelRefundModal(true);
					}}
					// A card refund already in flight can't be converted by a status flip. A failed one
					// carries a provider id but moved no money, so it stays eligible. A refund that is
					// itself paying out a credit voucher is never eligible — converting it back would
					// void one credit and issue another for the same money.
					showConvertButton={
						!refund.giftCertificateId &&
						(refund.status === 'FAILED' ||
							(refund.status === 'PENDING_MANUAL' && stringIsEmpty(refund.providerRefundId)))
					}
					onConvertClick={() => {
						setSelectedConvertRefund({
							id: refund.id,
							amount: refund.amount,
						});
						setShowConvertRefundModal(true);
					}}
				/>
			));
		}

		// Non-manager view
		return pendingRefunds.map(refund => <RefundItem key={refund.id} refund={refund} />);
	};

	return (
		<div className="refunds-section">
			{/* Summary Header */}
			<If condition={pendingRefunds.length > 1}>
				<MessageBox
					status="warning"
					title={`${pendingRefunds.length} Pending Refund${pendingRefunds.length !== 1 ? 's' : ''}`}
					actions={<Icon name={isExpanded ? 'expand_less' : 'expand_more'} useMaterialIcons />}
					onClick={() => {
						setIsExpanded(!isExpanded);
					}}
				>
					{`Total: ${formatCurrency(totalRefundAmount)}`}
				</MessageBox>
			</If>

			{/* Expanded Details */}
			{(isExpanded || pendingRefunds.length === 1) && <div className="refunds-details">{renderRefunds()}</div>}

			{showRefundModal && selectedRefund && (
				<ManualRefundModal
					refund={selectedRefund}
					refresh={refresh}
					onClose={() => setShowRefundModal(false)}
				/>
			)}

			{showConvertRefundModal && selectedConvertRefund && (
				<ConvertRefundToCreditModal
					refund={selectedConvertRefund}
					refresh={refresh}
					onClose={() => setShowConvertRefundModal(false)}
				/>
			)}
			{showCancelRefundModal && selectedCancelRefund && (
				<CancelManualRefundModal
					refund={selectedCancelRefund}
					refresh={refresh}
					onClose={() => setShowCancelRefundModal(false)}
				/>
			)}
		</div>
	);
}
